import type { Role } from './types';
import { ApiError } from './client';

/**
 * TENANT ISOLATION — client side.
 *
 * This module is the ONLY place the UI decides whether a principal may touch a
 * building. Every tenant-scoped API function calls `assertBuildingAccess` before
 * reading or writing anything.
 *
 * It is deliberately paranoid, but understand what it is and is not:
 *
 *   It IS   a correctness guard that stops the UI from ever issuing a
 *           cross-tenant request, and a second line of defence if a component
 *           passes the wrong id.
 *
 *   It is NOT security. Anyone can edit localStorage or call the API directly
 *           with curl. The server MUST re-derive the principal from the session
 *           and filter every query by building_id. See SECURITY.md §2.
 */

export type Principal = {
  roles: Role[];
  /** The single building a manager/resident belongs to. Null for admins. */
  buildingId: number | null;
  /** Buildings this principal may read. Empty means "all" (admin only). */
  buildingIds: number[];
};

const ANONYMOUS: Principal = { roles: [], buildingId: null, buildingIds: [] };

let current: Principal = ANONYMOUS;

/** Called by AuthProvider on login, restore, and logout. */
export function setPrincipal(principal: Principal | null): void {
  current = principal ?? ANONYMOUS;
}

export function getPrincipal(): Principal {
  return current;
}

export function isAdmin(principal: Principal = current): boolean {
  return principal.roles.includes('admin');
}

export function isManager(principal: Principal = current): boolean {
  return principal.roles.includes('manager');
}

/** Buildings the principal may read. `null` means every building. */
export function accessibleBuildingIds(principal: Principal = current): number[] | null {
  if (isAdmin(principal)) return null;
  if (principal.buildingIds.length > 0) return principal.buildingIds;
  return principal.buildingId === null ? [] : [principal.buildingId];
}

export function canAccessBuilding(buildingId: number, principal: Principal = current): boolean {
  const allowed = accessibleBuildingIds(principal);
  return allowed === null || allowed.includes(buildingId);
}

/**
 * Throws 403 unless the current principal may read this building.
 * Mirrors exactly what the server must do for the same request.
 */
export function assertBuildingAccess(buildingId: number): void {
  if (current.roles.length === 0) {
    throw new ApiError(401, 'Not authenticated', ['errors.unauthenticated']);
  }
  if (!Number.isSafeInteger(buildingId) || buildingId <= 0) {
    throw new ApiError(400, 'Invalid building id', ['errors.unexpected']);
  }
  if (!canAccessBuilding(buildingId)) {
    throw new ApiError(403, 'Cross-tenant access denied', ['errors.forbidden']);
  }
}

/** Throws 403 unless the principal holds one of the given roles. */
export function assertRole(...allowed: Role[]): void {
  if (!current.roles.some((role) => allowed.includes(role))) {
    throw new ApiError(403, 'Role not permitted', ['errors.forbidden']);
  }
}

/** Write operations on a building require admin or that building's manager. */
export function assertCanManage(buildingId: number): void {
  assertBuildingAccess(buildingId);
  if (!isAdmin() && !isManager()) {
    throw new ApiError(403, 'Manager role required', ['errors.forbidden']);
  }
}

/**
 * Last-line filter: drops any row whose buildingId does not match, so a bug in
 * the store can never leak another tenant's data into a rendered list.
 */
export function scopeToBuilding<T extends { buildingId: number }>(
  rows: T[],
  buildingId: number,
): T[] {
  return rows.filter((row) => row.buildingId === buildingId);
}
